import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { jobsAPI, questionnairesAPI } from '../../utils/api';
import RecruiterHeader from './RecruiterHeader';
import JobEditor from './JobEditor';
import ShareJobDropdown from '../shared/ShareJobDropdown';
import { Drawer } from '../shared/DrawerManager';
import './RecruiterDashboard.css';
import './JobsManagement.css';

const JobsManagement = () => {
  const navigate = useNavigate();
  useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingJobId, setEditingJobId] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showQuestionnaireDrawer, setShowQuestionnaireDrawer] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(false);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await jobsAPI.getAll();
      setJobs(response.data.jobs || []);
    } catch (err) {
      console.error('[JobsManagement] Error loading jobs:', err);
      setError(err.response?.data?.error || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (jobId) => {
    console.log('[JobsManagement] Opening editor for job ID:', jobId);
    setEditingJobId(jobId);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingJobId(null);
  };

  const handleSaveJob = () => {
    setSuccess('Job updated successfully!');
    setTimeout(() => setSuccess(''), 3000);
    loadJobs();
  };

  const handleCreateNew = () => {
    setEditingJobId(null);
    setShowEditor(true);
  };

  const handlePublish = async (jobId) => {
    if (!window.confirm('Publish this job posting? It will be visible to the public.')) {
      return;
    }

    try {
      await jobsAPI.publish(jobId);
      setSuccess('Job published successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadJobs();
    } catch (err) {
      console.error('[JobsManagement] Error publishing job:', err);
      setError(err.response?.data?.error || 'Failed to publish job');
    }
  };

  const handleClose = async (jobId) => {
    if (!window.confirm('Close this job posting? It will stop accepting applications.')) {
      return;
    }

    try {
      await jobsAPI.close(jobId);
      setSuccess('Job closed successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadJobs();
    } catch (err) {
      console.error('[JobsManagement] Error closing job:', err);
      setError(err.response?.data?.error || 'Failed to close job');
    }
  };

  const handleDelete = async (jobId, title) => {
    if (!window.confirm(`Delete job "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await jobsAPI.delete(jobId);
      setSuccess('Job deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadJobs();
    } catch (err) {
      console.error('[JobsManagement] Error deleting job:', err);
      setError(err.response?.data?.error || 'Failed to delete job');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { backgroundColor: '#6c757d', color: 'white' },
      active: { backgroundColor: '#28a745', color: 'white' },
      closed: { backgroundColor: '#dc3545', color: 'white' },
      paused: { backgroundColor: '#ffc107', color: 'black' }
    };

    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase',
        ...(styles[status] || styles.draft)
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard recruiter-dashboard">
      <RecruiterHeader currentView="jobs" />

      <main className="jobs-management-container">
        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>Manage Job Postings</h2>
            <p style={{ color: '#666', marginTop: '0.5rem' }}>
              Edit job details, slugs, and control visibility
            </p>
          </div>
          <button
            className="action-btn publish-btn"
            onClick={handleCreateNew}
            style={{ padding: '0.75rem 1.5rem' }}
          >
            + Create New Job
          </button>
        </div>

        {error && (
          <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#d4edda', color: '#155724', borderRadius: '4px' }}>
            {success}
          </div>
        )}

        <div className="jobs-table-wrapper">
          <table className="jobs-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Location</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Applications</th>
                <th style={{ textAlign: 'center' }}>Public</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    No jobs found
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job._id}>
                    <td>
                      <div
                        style={{ fontWeight: '500', cursor: 'pointer', color: '#1E40AF' }}
                        onClick={() => navigate(`/recruiter/jobs/${job._id}/applications`)}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      >
                        {job.title}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                        {job.slug}
                      </div>
                    </td>
                    <td>{job.location}</td>
                    <td>{getStatusBadge(job.status)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => navigate(`/recruiter/jobs/${job._id}/applications`)}
                        style={{
                          background: job.applicationCount > 0 ? '#10B981' : '#6B7280',
                          color: 'white',
                          border: 'none',
                          padding: '0.4rem 0.8rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = 'none';
                        }}
                        title="View Applications"
                      >
                        {job.applicationCount || 0}
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {job.isPublic ? (
                        <span style={{
                          background: '#D1FAE5',
                          color: '#065F46',
                          fontSize: '0.75rem',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}>Public</span>
                      ) : (
                        <span style={{
                          background: '#F3F4F6',
                          color: '#6B7280',
                          fontSize: '0.75rem',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}>Private</span>
                      )}
                    </td>
                    <td className="actions-cell" style={{ position: 'relative' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={async () => {
                            setSelectedJob(job);
                            setLoadingQuestionnaires(true);
                            setShowQuestionnaireDrawer(true);
                            try {
                              const result = await questionnairesAPI.getAll({ jobPostingId: job._id });
                              setQuestionnaires(result.data.questionnaires || []);
                            } catch (err) {
                              console.error('Failed to load questionnaires:', err);
                            } finally {
                              setLoadingQuestionnaires(false);
                            }
                          }}
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: '#8B5CF6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '500'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#7C3AED';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#8B5CF6';
                          }}
                          title="Manage Questionnaires"
                        >
                          Questionnaires
                        </button>
                        <ShareJobDropdown job={job} compact />
                        <button
                          onClick={() => handleEdit(job._id)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: '#3B82F6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '500'
                          }}
                          title="Edit Job"
                        >
                          Edit
                        </button>
                        {job.status === 'active' && (
                          <button
                            onClick={() => handleClose(job._id)}
                            style={{
                              padding: '0.4rem 0.8rem',
                              background: '#F59E0B',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: '500'
                            }}
                            title="Close this position"
                          >
                            Close
                          </button>
                        )}
                        {job.status !== 'active' && (
                          <button
                            onClick={() => handlePublish(job._id)}
                            style={{
                              padding: '0.4rem 0.8rem',
                              background: '#10B981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: '500'
                            }}
                            title="Publish this position"
                          >
                            Publish
                          </button>
                        )}
                        {(!job.applicationCount || job.applicationCount === 0) && (
                          <button
                            onClick={() => handleDelete(job._id, job.title)}
                            style={{
                              padding: '0.4rem 0.8rem',
                              background: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: '500'
                            }}
                            title="Delete this job"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Job Editor Modal */}
        {showEditor && (
          <JobEditor
            jobId={editingJobId}
            onClose={handleCloseEditor}
            onSave={handleSaveJob}
          />
        )}

        {/* Questionnaires Drawer */}
        {showQuestionnaireDrawer && selectedJob && (
          <Drawer
            isOpen={true}
            onClose={() => {
              setShowQuestionnaireDrawer(false);
              setSelectedJob(null);
              setQuestionnaires([]);
            }}
            title={`Questionnaires — ${selectedJob.title}`}
            width="520px"
          >
            <div style={{ padding: '1.5rem' }}>
              {loadingQuestionnaires ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                  Loading questionnaires...
                </div>
              ) : questionnaires.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📋</div>
                  <p style={{ color: '#6B7280', marginBottom: '0.5rem', fontWeight: '500' }}>
                    No questionnaires yet
                  </p>
                  <p style={{ color: '#9CA3AF', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                    Create a screening questionnaire to assess candidates for this role.
                  </p>
                  <button
                    onClick={() => {
                      setShowQuestionnaireDrawer(false);
                      navigate(`/recruiter/jobs/${selectedJob._id}/questionnaires/create`);
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#8B5CF6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                  >
                    + Create Questionnaire
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                    <span style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                      {questionnaires.length} questionnaire{questionnaires.length !== 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={() => {
                        setShowQuestionnaireDrawer(false);
                        navigate(`/recruiter/jobs/${selectedJob._id}/questionnaires/create`);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#8B5CF6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '0.8125rem'
                      }}
                    >
                      + New
                    </button>
                  </div>

                  {questionnaires.map((q) => (
                    <div
                      key={q._id}
                      style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '10px',
                        padding: '1.25rem',
                        marginBottom: '1rem',
                        background: '#FAFAFA',
                        transition: 'border-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = '#8B5CF6'}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = '#E5E7EB'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '1rem', color: '#111827' }}>{q.title}</h4>
                          {q.description && (
                            <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: '#6B7280', lineHeight: '1.4' }}>
                              {q.description.substring(0, 120)}{q.description.length > 120 ? '...' : ''}
                            </p>
                          )}
                        </div>
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          flexShrink: 0,
                          marginLeft: '0.75rem',
                          ...(q.status === 'active'
                            ? { background: '#D1FAE5', color: '#065F46' }
                            : q.status === 'draft'
                            ? { background: '#F3F4F6', color: '#6B7280' }
                            : { background: '#FEF3C7', color: '#92400E' })
                        }}>
                          {q.status}
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.8125rem', color: '#6B7280', marginBottom: '1rem' }}>
                        <span>{q.questions?.length || 0} questions</span>
                        <span>{q.deadline || 48}h deadline</span>
                        <span>{q.allowResubmission ? 'Resubmit OK' : 'One attempt'}</span>
                      </div>

                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => {
                            setShowQuestionnaireDrawer(false);
                            navigate(`/recruiter/jobs/${selectedJob._id}/questionnaires/${q._id}/edit`);
                          }}
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: 'white',
                            color: '#374151',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.8125rem',
                            fontWeight: '500'
                          }}
                        >
                          Edit
                        </button>
                        {q.status === 'draft' && (
                          <button
                            onClick={async () => {
                              try {
                                await questionnairesAPI.update(q._id, { status: 'active' });
                                const result = await questionnairesAPI.getAll({ jobPostingId: selectedJob._id });
                                setQuestionnaires(result.data.questionnaires || []);
                              } catch (err) {
                                console.error('Failed to activate questionnaire:', err);
                              }
                            }}
                            style={{
                              padding: '0.4rem 0.8rem',
                              background: '#10B981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.8125rem',
                              fontWeight: '500'
                            }}
                          >
                            Activate
                          </button>
                        )}
                        {q.status === 'active' && (
                          <button
                            onClick={async () => {
                              try {
                                await questionnairesAPI.update(q._id, { status: 'archived' });
                                const result = await questionnairesAPI.getAll({ jobPostingId: selectedJob._id });
                                setQuestionnaires(result.data.questionnaires || []);
                              } catch (err) {
                                console.error('Failed to archive questionnaire:', err);
                              }
                            }}
                            style={{
                              padding: '0.4rem 0.8rem',
                              background: 'white',
                              color: '#92400E',
                              border: '1px solid #FCD34D',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.8125rem',
                              fontWeight: '500'
                            }}
                          >
                            Archive
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete questionnaire "${q.title}"?`)) return;
                            try {
                              await questionnairesAPI.delete(q._id);
                              const result = await questionnairesAPI.getAll({ jobPostingId: selectedJob._id });
                              setQuestionnaires(result.data.questionnaires || []);
                            } catch (err) {
                              console.error('Failed to delete questionnaire:', err);
                            }
                          }}
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: 'white',
                            color: '#DC2626',
                            border: '1px solid #FCA5A5',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.8125rem',
                            fontWeight: '500'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Drawer>
        )}
      </main>
    </div>
  );
};

export default JobsManagement;
